import React, { useMemo, useRef, useState } from "react";
import {
  Download,
  Upload,
  AlertTriangle,
  Loader2,
  FileJson,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import employeesCanonical from "@/api/employees-canonical";
import { formatApiError } from "@/api/client";

/**
 * Phase 10.6 — Modal to import a canonicals-export JSON.
 *
 * The flow is: pick file → validate structure → show preview counts →
 * choose merge/replace → confirm → POST to backend.
 *
 * Merge is idempotent (running twice with the same payload is a no-op).
 * Replace wipes both `employees_canonical` and `employee_name_blacklist`
 * before reinserting — guarded behind an extra confirmation checkbox so a
 * misclick can't nuke production.
 */
export default function ImportCanonicalsDialog({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [payload, setPayload] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [mode, setMode] = useState("merge");
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setPayload(null);
    setParseError(null);
    setMode("merge");
    setConfirmReplace(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  // Phase 10.8 — sniff the first bytes of the file to identify the real
  // format so we can give the admin an actionable Spanish error instead
  // of the raw `JSON.parse()` complaint. `.xlsx` is a ZIP under the
  // hood, so its magic bytes are `PK` (0x50 0x4b). Anything else falls
  // through to the generic "not JSON" branch.
  const detectFormat = async (f) => {
    try {
      const first4 = await f.slice(0, 4).arrayBuffer();
      const bytes = new Uint8Array(first4);
      const isZip =
        bytes.length >= 2 && bytes[0] === 0x50 && bytes[1] === 0x4b;
      if (isZip) return "xlsx-or-zip";
      // A JSON document starts with '{' or '[' after optional whitespace.
      const c = String.fromCharCode(bytes[0]);
      if (c === "{" || c === "[") return "json-like";
      // BOM
      if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf)
        return "json-like";
      return "other";
    } catch {
      return "other";
    }
  };

  const handleFile = async (f) => {
    setFile(f);
    setPayload(null);
    setParseError(null);
    if (!f) return;

    // Extension + magic-bytes double-check. The `accept=".json"` on the
    // input is only a *hint* to the OS file picker; users can still
    // drag/drop or paste anything, so we validate on our side too.
    const format = await detectFormat(f);
    const lowerName = (f.name || "").toLowerCase();
    if (format === "xlsx-or-zip" || lowerName.endsWith(".xlsx") || lowerName.endsWith(".zip")) {
      setParseError(
        'Este archivo parece ser un Excel/reporte (.xlsx o .zip). ' +
          'El botón "Importar" solo acepta el archivo .json que se descarga con ' +
          '"Exportar (JSON)" de otro ambiente.\n\n' +
          '¿Estás buscando cargar un reporte de transacciones? Usa "Cargar reporte" en el menú lateral.',
      );
      return;
    }
    if (format === "other") {
      setParseError(
        "El archivo debe ser .json exportado con el botón " +
          '"Exportar (JSON)". Tipo detectado: no reconocido.',
      );
      return;
    }

    // At this point the file looks JSON-shaped — try parsing.
    let parsed;
    try {
      const text = await f.text();
      parsed = JSON.parse(text);
    } catch (err) {
      setParseError(
        `No se pudo leer el JSON: ${err.message}. ` +
          "Asegúrate de que el archivo sea el .json exportado y no esté corrupto.",
      );
      return;
    }

    // Structural validation — the export always carries these three keys.
    // Missing any of them means it's a foreign JSON (a Postman export,
    // a Shopify metafields dump, etc.), not our canonicals payload.
    const missing = [];
    if (typeof parsed?.version === "undefined") missing.push("version");
    if (!Array.isArray(parsed?.canonicals)) missing.push("canonicals[]");
    if (!Array.isArray(parsed?.blocked_names)) missing.push("blocked_names[]");
    if (missing.length) {
      setParseError(
        "El archivo es JSON válido pero no parece un export de canónicos. " +
          `Debe tener las claves: version, canonicals[], blocked_names[]. ` +
          `Faltan: ${missing.join(", ")}.`,
      );
      return;
    }

    setPayload(parsed);
  };

  const previewCounts = useMemo(() => {
    if (!payload) return null;
    return {
      canonicals: payload.canonicals?.length ?? 0,
      blocked: payload.blocked_names?.length ?? 0,
      env: payload.source_env || "desconocido",
      exportedAt: payload.exported_at,
    };
  }, [payload]);

  const doImport = async () => {
    if (!payload) return;
    if (mode === "replace" && !confirmReplace) {
      toast.error("Marca la casilla de confirmación para el modo replace.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await employeesCanonical.import(payload, {
        mode,
        confirm: mode === "replace",
      });
      toast.success(
        `Importados: ${data.imported_canonicals} nuevos, ${data.merged_canonicals} mergeados. ` +
          `Bloqueados: ${data.imported_blocked} nuevos, ${data.merged_blocked} actualizados. ` +
          `Errores: ${data.errors?.length ?? 0}.`,
        { duration: 8000 },
      );
      if (data.errors?.length) {
        console.warn("Import errors:", data.errors);
        toast.warning(
          `${data.errors.length} error(es) al importar — revisa la consola.`,
          { duration: 6000 },
        );
      }
      onImported?.(data);
      handleClose(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg"
        data-testid="import-canonicals-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Importar empleados canónicos
          </DialogTitle>
          <DialogDescription>
            Restaura la lista completa desde un archivo JSON exportado
            previamente (útil para migrar preview → producción).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="import-file" className="text-xs">
              Archivo JSON
            </Label>
            <input
              ref={inputRef}
              id="import-file"
              type="file"
              accept=".json,application/json"
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
              disabled={loading}
              data-testid="import-file-input"
              className="mt-1 block w-full cursor-pointer text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-primary-foreground hover:file:bg-primary/90"
            />
            {file && (
              <p className="mt-1 font-mono text-xs text-muted-foreground">
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {parseError && (
            <Alert
              variant="destructive"
              data-testid="import-parse-error"
              className="border-destructive/50"
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Archivo inválido</AlertTitle>
              <AlertDescription className="whitespace-pre-line text-xs">
                {parseError}
              </AlertDescription>
            </Alert>
          )}

          {previewCounts && (
            <Alert data-testid="import-preview" className="border-emerald-500/40">
              <FileJson className="h-4 w-4 text-emerald-500" />
              <AlertTitle>Archivo detectado</AlertTitle>
              <AlertDescription className="text-xs">
                <span
                  className="font-mono"
                  data-testid="import-preview-canonicals"
                >
                  {previewCounts.canonicals}
                </span>{" "}
                canónicos ·{" "}
                <span
                  className="font-mono"
                  data-testid="import-preview-blocked"
                >
                  {previewCounts.blocked}
                </span>{" "}
                bloqueados.
                <br />
                Origen:{" "}
                <span className="font-mono">{previewCounts.env}</span> ·
                Exportado:{" "}
                <span className="font-mono">
                  {previewCounts.exportedAt?.slice(0, 19).replace("T", " ") ||
                    "—"}
                </span>
              </AlertDescription>
            </Alert>
          )}

          {previewCounts && (
            <div>
              <Label className="text-xs">Modo de importación</Label>
              <RadioGroup
                value={mode}
                onValueChange={(v) => {
                  setMode(v);
                  setConfirmReplace(false);
                }}
                className="mt-2 space-y-2"
                data-testid="import-mode-radio"
              >
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-3 hover:bg-accent/40">
                  <RadioGroupItem value="merge" id="mode-merge" data-testid="import-mode-merge" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      Merge (recomendado)
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Une aliases sin duplicar. Los canónicos existentes se
                      preservan. Idempotente.
                    </p>
                  </div>
                </label>
                <label className="flex cursor-pointer items-start gap-2 rounded-md border border-destructive/40 p-3 hover:bg-destructive/5">
                  <RadioGroupItem value="replace" id="mode-replace" data-testid="import-mode-replace" />
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Replace (destructivo)
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Borra TODOS los canónicos y bloqueados actuales antes
                      de reinsertar. Úsalo solo para migración inicial.
                    </p>
                  </div>
                </label>
              </RadioGroup>
              {mode === "replace" && (
                <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs">
                  <input
                    type="checkbox"
                    checked={confirmReplace}
                    onChange={(e) => setConfirmReplace(e.target.checked)}
                    data-testid="import-confirm-replace"
                    className="h-4 w-4"
                  />
                  Confirmo que quiero borrar los datos actuales y reemplazarlos.
                </label>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={loading}
            data-testid="import-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            onClick={doImport}
            disabled={
              !payload ||
              loading ||
              (mode === "replace" && !confirmReplace)
            }
            data-testid="import-confirm-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Importando…
              </>
            ) : (
              <>
                <Download className="mr-1.5 h-3.5 w-3.5 rotate-180" />
                Importar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
